import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useLanguage } from '../context/LanguageContext';
import { Mail, Plane, Ship, Truck, Send, CheckCircle, Phone, MessageCircle } from 'lucide-react';

const QuotePage = () => {
  const { language } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    origin: '',
    destination: '',
    cargoType: '',
    weight: '',
    transport: 'sea',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const content = {
    pt: {
      title: 'Solicite sua Cotação',
      subtitle: 'Preencha o formulário e receba uma cotação personalizada em até 24 horas',
      transport: 'Tipo de Transporte',
      transportTypes: [
        { id: 'sea', label: 'Marítimo' },
        { id: 'air', label: 'Aéreo' },
        { id: 'road', label: 'Rodoviário' },
      ],
      cargoTypes: ['Carga Geral', 'Carga Refrigerada', 'Carga Perigosa', 'Carga Projeto', 'Grãos/Commodities', 'Outros'],
      fields: {
        name: 'Nome completo',
        email: 'E-mail',
        phone: 'Telefone',
        company: 'Empresa',
        origin: 'Origem',
        destination: 'Destino',
        cargoType: 'Tipo de Carga',
        weight: 'Peso estimado (kg)',
        message: 'Informações adicionais',
      },
      placeholders: {
        name: 'Seu nome',
        email: 'seu@email.com',
        phone: '+55 (00) 00000-0000',
        company: 'Nome da empresa',
        origin: 'Ex: São Paulo, Brasil',
        destination: 'Ex: Rotterdam, Holanda',
        weight: 'Ex: 1000',
        message: 'Descreva detalhes da sua carga, dimensões, necessidades especiais...',
      },
      submit: 'Enviar Solicitação',
      submitting: 'Enviando...',
      successTitle: 'Solicitação Enviada!',
      successMessage: 'Recebemos sua solicitação de cotação. Nossa equipe entrará em contato em até 24 horas através do e-mail ou telefone informado.',
      newQuote: 'Nova Cotação',
      contact: {
        title: 'Prefere falar diretamente?',
        email: 'sales@wellcargo.com.br',
        phone: '+55 27 99758-0648',
        whatsapp: '+55 27 99758-0648',
      },
    },
    en: {
      title: 'Request a Quote',
      subtitle: 'Fill out the form and receive a personalized quote within 24 hours',
      transport: 'Transport Type',
      transportTypes: [
        { id: 'sea', label: 'Sea' },
        { id: 'air', label: 'Air' },
        { id: 'road', label: 'Road' },
      ],
      cargoTypes: ['General Cargo', 'Refrigerated Cargo', 'Hazardous Cargo', 'Project Cargo', 'Grains/Commodities', 'Others'],
      fields: {
        name: 'Full name',
        email: 'Email',
        phone: 'Phone',
        company: 'Company',
        origin: 'Origin',
        destination: 'Destination',
        cargoType: 'Cargo Type',
        weight: 'Estimated weight (kg)',
        message: 'Additional information',
      },
      placeholders: {
        name: 'Your name',
        email: 'your@email.com',
        phone: '+55 (00) 00000-0000',
        company: 'Company name',
        origin: 'Ex: São Paulo, Brazil',
        destination: 'Ex: Rotterdam, Netherlands',
        weight: 'Ex: 1000',
        message: 'Describe cargo details, dimensions, special needs...',
      },
      submit: 'Send Request',
      submitting: 'Sending...',
      successTitle: 'Request Sent!',
      successMessage: 'We received your quote request. Our team will contact you within 24 hours via the email or phone provided.',
      newQuote: 'New Quote',
      contact: {
        title: 'Prefer to talk directly?',
        email: 'sales@wellcargo.com.br',
        phone: '+55 27 99758-0648',
        whatsapp: '+55 27 99758-0648',
      },
    },
    es: {
      title: 'Solicite su Cotización',
      subtitle: 'Complete el formulario y reciba una cotización personalizada en hasta 24 horas',
      transport: 'Tipo de Transporte',
      transportTypes: [
        { id: 'sea', label: 'Marítimo' },
        { id: 'air', label: 'Aéreo' },
        { id: 'road', label: 'Terrestre' },
      ],
      cargoTypes: ['Carga General', 'Carga Refrigerada', 'Carga Peligrosa', 'Carga Proyecto', 'Granos/Commodities', 'Otros'],
      fields: {
        name: 'Nombre completo',
        email: 'Correo electrónico',
        phone: 'Teléfono',
        company: 'Empresa',
        origin: 'Origen',
        destination: 'Destino',
        cargoType: 'Tipo de Carga',
        weight: 'Peso estimado (kg)',
        message: 'Información adicional',
      },
      placeholders: {
        name: 'Su nombre',
        email: 'su@email.com',
        phone: '+55 (00) 00000-0000',
        company: 'Nombre de la empresa',
        origin: 'Ej: São Paulo, Brasil',
        destination: 'Ej: Rotterdam, Holanda',
        weight: 'Ej: 1000',
        message: 'Describa detalles de su carga, dimensiones, necesidades especiales...',
      },
      submit: 'Enviar Solicitud',
      submitting: 'Enviando...',
      successTitle: '¡Solicitud Enviada!',
      successMessage: 'Recibimos su solicitud de cotización. Nuestro equipo se comunicará dentro de 24 horas a través del correo o teléfono proporcionado.',
      newQuote: 'Nueva Cotización',
      contact: {
        title: '¿Prefiere hablar directamente?',
        email: 'sales@wellcargo.com.br',
        phone: '+55 27 99758-0648',
        whatsapp: '+55 27 99758-0648',
      },
    },
  };

  const c = content[language];

  const transportIcons = {
    sea: Ship,
    air: Plane,
    road: Truck,
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Construir o corpo do email
    const transportLabel = c.transportTypes.find(t => t.id === formData.transport)?.label || formData.transport;
    
    const emailBody = `
Nova Solicitação de Cotação - Well Cargo

DADOS DO SOLICITANTE:
Nome: ${formData.name}
E-mail: ${formData.email}
Telefone: ${formData.phone}
Empresa: ${formData.company}

DADOS DA CARGA:
Tipo de Transporte: ${transportLabel}
Origem: ${formData.origin}
Destino: ${formData.destination}
Tipo de Carga: ${formData.cargoType}
Peso Estimado: ${formData.weight} kg

INFORMAÇÕES ADICIONAIS:
${formData.message || 'Nenhuma informação adicional'}
    `.trim();

    const mailtoLink = `mailto:sales@wellcargo.com.br?subject=${encodeURIComponent(`Solicitação de Cotação - ${formData.company || formData.name}`)}&body=${encodeURIComponent(emailBody)}`;

    // Simular envio e abrir o cliente de email
    setTimeout(() => {
      window.location.href = mailtoLink;
      setIsSubmitting(false);
      setIsSubmitted(true);
    }, 1000);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      company: '',
      origin: '',
      destination: '',
      cargoType: '',
      weight: '',
      transport: 'sea',
      message: '',
    });
    setIsSubmitted(false);
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors duration-300">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-[#1a6b5c] to-[#0f4a3f]">
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(15)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-white/20 rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animation: `twinkle ${2 + Math.random() * 2}s infinite`,
              }}
            />
          ))}
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Mail className="w-16 h-16 mx-auto text-[#3ECDA3] mb-6" />
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold italic text-white mb-4">
            {c.title}
          </h1>
          <p className="text-[#3ECDA3] text-lg">
            {c.subtitle}
          </p>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {isSubmitted ? (
            /* Success Message */
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 text-center">
              <div className="w-20 h-20 bg-[#3ECDA3]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-[#3ECDA3]" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                {c.successTitle}
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-8 max-w-md mx-auto">
                {c.successMessage}
              </p>
              <button
                onClick={resetForm}
                className="bg-[#3ECDA3] hover:bg-[#35b892] text-white px-8 py-3 rounded-lg font-medium transition-all"
              >
                {c.newQuote}
              </button>
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8">
              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Transport Type Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
                    {c.transport}
                  </label>
                  <div className="grid grid-cols-3 gap-4">
                    {c.transportTypes.map((type) => {
                      const IconComponent = transportIcons[type.id];
                      return (
                        <button
                          key={type.id}
                          type="button"
                          onClick={() => setFormData({ ...formData, transport: type.id })}
                          className={`p-6 rounded-xl border-2 transition-all duration-300 ${
                            formData.transport === type.id
                              ? 'border-[#3ECDA3] bg-[#3ECDA3]/10'
                              : 'border-gray-200 dark:border-gray-700 hover:border-[#3ECDA3]/50'
                          }`}
                        >
                          <IconComponent className={`w-8 h-8 mx-auto mb-2 ${
                            formData.transport === type.id ? 'text-[#3ECDA3]' : 'text-gray-400'
                          }`} />
                          <span className={`block text-sm font-medium ${
                            formData.transport === type.id ? 'text-[#3ECDA3]' : 'text-gray-600 dark:text-gray-400'
                          }`}>
                            {type.label}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Personal Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {c.fields.name} *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder={c.placeholders.name}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[#3ECDA3] focus:border-transparent dark:bg-gray-700 dark:text-white outline-none transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {c.fields.email} *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder={c.placeholders.email}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[#3ECDA3] focus:border-transparent dark:bg-gray-700 dark:text-white outline-none transition-all"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {c.fields.phone} *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      placeholder={c.placeholders.phone}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[#3ECDA3] focus:border-transparent dark:bg-gray-700 dark:text-white outline-none transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {c.fields.company}
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder={c.placeholders.company}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[#3ECDA3] focus:border-transparent dark:bg-gray-700 dark:text-white outline-none transition-all"
                    />
                  </div>
                </div>

                {/* Origin & Destination */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {c.fields.origin} *
                    </label>
                    <input
                      type="text"
                      name="origin"
                      value={formData.origin}
                      onChange={handleChange}
                      required
                      placeholder={c.placeholders.origin}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[#3ECDA3] focus:border-transparent dark:bg-gray-700 dark:text-white outline-none transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {c.fields.destination} *
                    </label>
                    <input
                      type="text"
                      name="destination"
                      value={formData.destination}
                      onChange={handleChange}
                      required
                      placeholder={c.placeholders.destination}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[#3ECDA3] focus:border-transparent dark:bg-gray-700 dark:text-white outline-none transition-all"
                    />
                  </div>
                </div>

                {/* Cargo Type & Weight */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {c.fields.cargoType} *
                    </label>
                    <select
                      name="cargoType"
                      value={formData.cargoType}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[#3ECDA3] focus:border-transparent dark:bg-gray-700 dark:text-white outline-none transition-all"
                    >
                      <option value="">Selecione...</option>
                      {c.cargoTypes.map((type, index) => (
                        <option key={index} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {c.fields.weight}
                    </label>
                    <input
                      type="text"
                      name="weight"
                      value={formData.weight}
                      onChange={handleChange}
                      placeholder={c.placeholders.weight}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[#3ECDA3] focus:border-transparent dark:bg-gray-700 dark:text-white outline-none transition-all"
                    />
                  </div>
                </div>

                {/* Message */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {c.fields.message}
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={4}
                    placeholder={c.placeholders.message}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[#3ECDA3] focus:border-transparent dark:bg-gray-700 dark:text-white outline-none transition-all resize-none"
                  />
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#3ECDA3] hover:bg-[#35b892] text-white py-4 rounded-lg font-medium transition-all duration-300 flex items-center justify-center gap-3 disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      <Send size={20} />
                      {c.submit}
                    </>
                  )}
                </button>
              </form>

              {/* Direct Contact */}
              <div className="mt-8 pt-8 border-t border-gray-200 dark:border-gray-700">
                <p className="text-center text-gray-600 dark:text-gray-400 mb-6">
                  {c.contact.title}
                </p>
                <div className="flex flex-wrap justify-center gap-4">
                  <a
                    href={`mailto:${c.contact.email}`}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-gray-100 dark:bg-gray-700 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-all"
                  >
                    <Mail size={18} className="text-[#3ECDA3]" />
                    {c.contact.email}
                  </a>
                  <a
                    href={`tel:${c.contact.phone.replace(/\s/g, '')}`}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-gray-100 dark:bg-gray-700 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-all"
                  >
                    <Phone size={18} className="text-[#3ECDA3]" />
                    {c.contact.phone}
                  </a>
                  <a
                    href={`https://wa.me/${c.contact.whatsapp.replace(/\D/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-green-500 rounded-lg text-white hover:bg-green-600 transition-all"
                  >
                    <MessageCircle size={18} />
                    WhatsApp
                  </a>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default QuotePage;
